import BaseModal from 'components/BaseModal'
import _ from 'lodash'
import axios from 'axios'
import { MODAL_SIZE_MEDIUM } from 'constants/Layout'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { FieldTypes, directoryRegexValidator, nameRegexValidator } from 'utils/formUtils'
import ProductAPI from 'restAPI/ProductAPI'
import SubProductAPI from 'restAPI/SubProductAPI'
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI'
import { Alert, Typography, Button } from 'antd'
import ModalUtil from 'utils/modalUtil'
import IngestionScriptSuccessInstructions from './IngestionScriptSuccessInstructions'
import IntegrationsAPI from 'restAPI/IntegrationsAPI'
import WizardFormV2 from 'components/WizardFormV2'
import Vocabulary from 'components/Vocabulary';
import { IntegrationType } from './common';
import moment from 'moment'
import { envParseSelectOptions } from 'views/Findings/Constants'
import { toolNameForAPI } from './GenericPullAndPushToolView';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext'
import { CUSTOM_PREFIX } from './CustomToolForm'

const modalProps = {
    footer: null,
    width: MODAL_SIZE_MEDIUM,
    // bodyStyle: {height: 500, paddingLeft: 90, paddingRight: 90, paddingTop: 90},
    style: { top: 80 },
}

const { Text } = Typography

export const ConfigurationInstructions = ({ toolName, onCancel, onStart }) => {
    return (
        <div className="d-flex flex-column">
            <Text className="text-bold scan-heading-dark">Configure {toolName} in three easy steps.</Text>
            <ul>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">Fill the details for the tool and download.</Text></div></li>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">Edit the script to set the ArmorCode API token. If you haven't generated an ArmorCode API token no worries you can generate it in the next step.</Text></div></li>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">Execute the script. You may also set the script to run automatically in your cron, jenkins etc.</Text></div></li>
            </ul>
            <Alert type="warning" className='m-b-l' description={<span>Uploading findings using push works as different finding and won't cause dedupe/<Vocabulary name="mitigation" isLowerCase={true} /> for findings uploaded via other mechanism like Pull Or Upload.</span>}/>
            <div className="d-flex justify-content-between">
                <Button onClick={onCancel} type="default">Cancel</Button>
                <Button onClick={onStart} type="primary">Start</Button>
            </div>
        </div>
    )
}

export const ConfigurationSubmitPage = () => {
    return (
        <div className="d-flex flex-column m-t-l">
            <Text className="text-bold scan-heading-dark">You can execute the script via following methods.</Text>
            <ul>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">Execute the script via cron job.</Text></div></li>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">Execute the script via jenkins configuration.</Text></div></li>
                <li><div className="m-t-l m-b-l"><Text className="scan-heading">You can also run it manually.</Text></div></li>
            </ul>
        </div>
    )
}

export default forwardRef(function IngestionScriptFormPopup({name, ...props}, ref) {
    const isEdit = !!props.data;
    const isToolData = !!props.toolData
    const { allowZip } = props;
    const [products, setProducts] = useState();
    const [subProducts, setSubProducts] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [versions, setVersions] = useState([]);
    const [formData, setFormData] = useState({});
    const [form, setForm] = useState();
    const [tokenData, setTokenData] = useState();
    const [succeeded, setSucceeded] = useState();
    const [showWizard, setShowWizard] = useState(isEdit);
    const [list, setList] = useState([])
    const thisRef = useRef();
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const getProducts = useCallback(() =>{
        const { promise, cancel } = ProductAPI.getAllQuick();
        promise.then(setProducts, _.noop);
        return cancel;
    },[])
    const getversions = useCallback(() => {
        const {promise, cancel} = ToolsIntegrationsAPI.getVersion(name);
        promise.then(setVersions, _.noop);
        return cancel;
    },[name])

    const setInitialValue = (form, data) => {
        const { directory, environment, product_id, sub_product_id, version, tags } = data;
        const initialProductId = +product_id || undefined;
        const initialSubProductId = +sub_product_id || undefined;
        form.setFieldsValue({ folder_name: directory, environment, product: initialProductId, version, tags })
        const { promise: subProductPromise } = SubProductAPI.getAllQuick(product_id);
        subProductPromise.then(setSubProducts, _.noop);
        form.setFieldsValue({ subProduct: initialSubProductId })
        const { promise: environmentPromise } = SubProductEnvironmentTypeAPI.getAllQuick(sub_product_id);
        environmentPromise.then(response => {
            setEnvironments(response)
            const productionId = response.find(env => env.name === environment)?.id
            form.setFieldsValue({ environment:  productionId })
        })
    }
    useEffect(() => {
        getProducts();
        getversions();  
    }, [getProducts,getversions]);
    useEffect(() => {
        if(isEdit && form){
            setInitialValue(form, props.data)
        }
    }, [isEdit, form,props.data])
    useEffect(() => {
        const params = new URLSearchParams(props.history?.location.search);
        const paramMap = _.pickBy({
            product_id: params.get('product_id'),
            sub_product_id: params.get('sub_product_id'),
            environment: params.get('environment'),
        }, _.identity);

        if(isToolData && form){
            setInitialValue(form, props.toolData);
        }else if (isEdit && form){
            setInitialValue(form, props.data);
        } else if(form && !_.isEmpty(paramMap)) {
            setInitialValue(form, paramMap);
        }
    }, [props.data, isEdit, isToolData, form, props.toolData, props.history])
    useEffect(() => {
        if(formData.keyExists){
            form.resetFields(["expiry", "apiName"])
        }
    }, [formData.keyExists, form])
    useEffect(() => {
        if(formData.product && form){
            form.setFieldsValue({ subProduct:  undefined, environment: undefined });
            setSubProducts(null);
            const {promise, cancel} = SubProductAPI.getAllQuick(formData.product);
            promise.then(setSubProducts, _.noop);
            return cancel;
        }
    }, [formData.product, form]);
    useEffect(() => {
        if(formData.subProduct && form){
            form.setFieldsValue({ environment: undefined });
            setEnvironments(null);
            const {promise, cancel} = SubProductEnvironmentTypeAPI.getAllQuick(formData.subProduct);
            promise.then(response => {
                setEnvironments(response)
                const productionId = response.find(env => env.name === "Production")?.id
                form.setFieldsValue({ environment:  productionId })
            })
            return cancel;
        }
    }, [formData.subProduct, form])

    useEffect(()=>{
        const {promise, cancel} = IntegrationsAPI.getAllAPIKeys();
        promise.then(setList)
        return cancel
      },[])

    const tool = name.toLowerCase();
    useImperativeHandle(ref, () => ({ ...thisRef.current }));

    const processForm = useCallback((data) => {
        if(hasDisabledEnvironment){
            data.environment = environments.find(env => env.name === envValue)?.id;
        }
        return downLoadScript(data, products, subProducts, environments, tokenData, setTokenData, props.data);
    }, [products, subProducts, environments, tokenData, setTokenData, props.data, hasDisabledEnvironment, envValue]);

    const onSuccess = useCallback(({fileName, tokenData: _tokenData}) => {
        ModalUtil.show({
            content: <IngestionScriptSuccessInstructions fileName={fileName} token={{..._tokenData}} downloadFileType={formData.downloadFileType} />
        });
        setSucceeded(true);
        thisRef.current.close();
    }, [thisRef, formData])

    const pages = [{
        name: "Tool Details",
        fields: getCommonFields({ products, subProducts, environments, tool, versions, isEdit, allowZip, hasDisabledEnvironment })
    }, {
        name: "API Key",
        fields: getAPIKeyFields(formData,list)
    }, {
        name: "Execute script",
        fields:
        [
            {
                name: 'downloadFileType',
                label: "Download File Type",
                type: FieldTypes.RADIO,
                options: [{
                  value: "python",
                  label: "Python"
                },{
                  value: "bash",
                  label: "Bash"
                }],
            }
        ],
        showComponentBeforeField: false,
        component: ConfigurationSubmitPage,
    }]
    return (
        <BaseModal
            modalProps={isEdit ? {...modalProps, title: `Edit ${name} Configuration`} : {...modalProps, title: `Configure ${name}`}}
            ref={thisRef}
            afterClose={() => props.afterClose(succeeded)}
        >
            {!showWizard && <ConfigurationInstructions toolName={name} onCancel={thisRef.current?.close} onStart={() => setShowWizard(true)} />}
            {showWizard && <WizardFormV2
                // title={`Configure ${name}`}
                validateOnBackwardStep={false}
                onCancel={thisRef.current?.close}
                pages={pages}
                initialValues={{
                    file_format: ExtensionsMap[tool]?.[0] || ".csv",
                    tool:name,
                    downloadFileType: 'python'
                }}
                onChange={(data) => setFormData({...formData, ...data})}
                formRef={setForm}
                api={processForm}
                onSuccess={onSuccess}
            />}
        </BaseModal>
    )
})

export const downloadPreConfiguredScript =  (scriptPayload) => {
    if(!scriptPayload){
        return false;
    }
    const {
        product_name: product,
        sub_product_name: subProduct,
        directory: folder_name,
        file_extension: file_format,
        tool_name,
        ...rest
    } = scriptPayload;
    const requestPayload = {
        ...rest,
        product,
        subProduct,
        folder_name,
        file_format,
        preConfiguredScript: true,
    }
    if (tool_name.startsWith(CUSTOM_PREFIX)) {
        requestPayload.tool = tool_name;
    } else {
        requestPayload.tool = tool_name.toLowerCase().replaceAll("_", " ");
    }
    invokeScriptDownload(requestPayload);
}

export const invokeScriptDownload = async (scriptPayload) => { 
    let scriptDetails = {};
    const anchor = document.createElement("a");
    try{
        if(!scriptPayload){
            throw new Error('missing mandatory argument: scriptPayload');
        }
        const {
            preConfiguredScript = false,
            environment,
            product,
            subProduct,
            tool,
            folder_name,
            file_format,
            build,
            tags,
            allowZip,
            downloadFileType
        } = scriptPayload;
        
        let env = window.location.hostname.split(".")[0] || "stage";
        if(env !== "app"){
            env = "stage";
        }

        const scriptName = downloadFileType === 'bash' ? 'app.bashscript.txt' :`${env}${allowZip ? ".v2" : ""}.script.txt`;
        const getEnvironmentDetails = () => {
            if(preConfiguredScript === true){
                return new Promise((resolve) => {
                    resolve({
                        name: environment
                    });
                });
            }
            return SubProductEnvironmentTypeAPI.get(environment)
        }
        const promises = [
            axios.get(`/python-scripts/${scriptName}?v=${Date.now()}`),
            getEnvironmentDetails()
        ]
        const lowerCasetoolName = tool.toLowerCase();
        const [{ data: scriptText }, {name: envName}] = await Promise.all(promises);
        const scanTool = ScanNameMap[lowerCasetoolName] || ((tool.startsWith(CUSTOM_PREFIX) ? tool : _.capitalize(tool)) + ' Scan');
        const script = scriptText
            .substr(0, scriptText.length)
            .replace(/{__url__}/gi, window.location.protocol + "//" + window.location.hostname)
            .replace(/{__environment__}/gi, "\"" + envName + "\"")
            .replace(/{__download_time_date__}/gi, new Date())
            .replace(/{__product__}/gi, product)
            .replace(/{__sub_product__}/gi, subProduct)
            .replace(/{__scan_tool__}/gi, "\"" + scanTool + "\"")
            .replace(/{__scan_tool_display__}/gi, "\"" + (tool || _.capitalize(tool)) + "\"")
            .replace(/{__tags_space_seperated__}/gi,  "\"" + (tags?.join(" ") || "") + "\"")
            .replace(/{__folder_name__}/gi, "\"" + folder_name + "\"")
            // .replace(/{__file_name_prefix__}/gi, "\"" + (values.file_name_prefix || "") + "\"")
            .replace(/{__file_format__}/gi, "\"" + (_.isArray(file_format) ? file_format[0] : file_format) + "\"")
            .replace(/{__file_name__}/gi, "\"\"")
            .replace(/{__build__}/gi, "\"" + (build || "") + "\"");

        const fileName = downloadFileType === 'bash' ?
            `armorcode_import_${lowerCasetoolName.split(" ").join("_")}.sh`
            :
            `armorcode_import_${lowerCasetoolName.split(" ").join("_")}_${scriptName.includes("v2") ? "v2" : "v1"}.py`;
        scriptDetails = {
            ...scriptDetails,
            name: fileName, 
            data: script
        };
        document.body.append(anchor);
        anchor.download = fileName;
        const encoded = await encodeURIComponent(script);
        anchor.href = 'data:text/plain;charset=utf-8,' + encoded;
        anchor.click();
        anchor.remove();
    }catch(err){
        anchor.remove();
        console.error('Error in handleSubmit:', err);
        console.error('Err. invokeScriptDownload: %s',err);
    }
    return scriptDetails;
}

async function downLoadScript(values, products, subProducts, environments, token, setToken, toolData){
    const { product, subProduct, environment, tool,version, expiry: _expiryPeriod, tags, allowZip } = values;
    // eslint-disable-next-line no-useless-catch
    try{
        const expiry = _expiryPeriod === -1 ? null : moment().add(_expiryPeriod, 'days').endOf('D').valueOf();
        const _product = products.find(prd => prd.id === product)
        const _subProduct = subProducts.find(prd => prd.id === subProduct)
        const _environment = environments.find(env => env.id === environment)
        let tokenData;
            
        const tool_name = toolNameForAPI(tool);

        const payloadData = {
            tool_name,
            product_name: _product.name,
            sub_product_name: _subProduct.name,
            environment: _environment.name,
            directory: values.folder_name,
            version,
            // file_prefix: values.file_name_prefix,
            file_extension: _.isArray(values.file_format) ? values.file_format[0] : values.file_format,
            id: toolData?.id,
            toolType: IntegrationType.PUSH,
            tags,
            allowZip
        }
        
        toolData ? await ToolsIntegrationsAPI.updateGenericConfigurationByToolName(payloadData).promise : await ToolsIntegrationsAPI.postGenericToolsConfiguration([payloadData]).promise;

        if(!token && !values.keyExists){
            const data = await IntegrationsAPI.createNewAPIKey({ name: values.apiName, expiry,accessOnAllProducts: true, accountLevelToken: false }).promise;
            tokenData = {
                name: values.apiName,
                value: data.apiKey
            };
            setToken({ ...tokenData })
        }
        
        const { name: fileName } = await invokeScriptDownload({...values, product:_product.name,subProduct: _subProduct.name});
        return { fileName, tokenData };
    } catch (e) { 
        throw e;
    }
}

export const ExtensionsMap = {
    zap: [".xml", ".json"],
    bandit: [".json"],
    burpsuite: [".xml"],
    "dependency check": [".xml"],
    eslint: [".json"],
    fortify: [".xml", ".fpr"],
    sonarqube: [".html", ".json"],
    sonarcloud: [".html", ".json"],
    sonarqube_detailed: [".html", ".json"],
    "blackduck hub": [".zip",".csv"],
    checkmarx: [".xml", ".csv",".zip"],
    "jfrog xray": [".json", ".zip", ".csv"],
    checkmarx_detailed: [".xml"],
    "npm audit": [".json"],
    "qualys": [".xml"], 
    "qualys webapp": [".xml"],
    "qualys infrastructure": [".xml", ".zip"],
    acunetix: [".xml"], 
    appscan: [".xml", ".html",".zip"],
    metasploit: [".xml"],
    appspider: [".xml"],
    aqua: [".json"],
    "aws prowler": [".csv"],
    "aws security hub": [".json"],
    brakeman: [".json"],
    bugcrowd: [".csv"],
    clair: [".json"],
    cobalt: [".json",".csv"],
    contrast: [".xml"],
    gitleaks: [".json"],
    "microfocus webinspect": [".xml"],
    "mozilla observatory": [".json"],
    nessus: [".xml",".csv"],
    netsparker: [".json"],
    nikto: [".xml"],
    nmap: [".xml"],
    "openvas csv": [".csv"],
    snyk: [".json"],
    sqreen: [".xml"],
    trustwave: [".csv"],
    'prisma cloud twistlock': [".json",".csv"],
    veracode: [".xml", ".json"],
    "web inspect": [".xml"],
    mend: [".json"],
    "coverity":[".xml"],
    hackerone:[".json"],
    "nowsecure":[".json"],
    "qwiet ai": [".json"],
    "dependency track": [".json"],
    "cyclonedx": [".json", ".xml"],
    "rapid7 nexpose": [".xml"],
    "detect secrets": [".json"],
    "dependabot": [".json"],
    'github code scan': ['.json', '.zip'],
    'prisma cloud redlock':[".json"],
    "google cloud registry":[".yaml",".yml"],
    "avocado": [".json"],
    "lacework":[".json"],
    "vm securestate": [".json"],
    "sonatype lifecycle":[".json"],
    "magento":[".pdf"],
    "rapid7 insight": [".html",".csv",".json"],
    "kics":[".json"],
    "contrast security": [".zip", ".csv", ".json", ".sarif"],
    "tenable webapp": [".json"],
    "tenable infrastructure": [".nessus", ".json"],
    "tenable vulnerability management":  [".csv",".nessus",".json"],
    "yarn audit": [".json"],
    "bundler audit": [".json"],
    "scout suite":[".js"],
    "wiz":[".csv",".json"],
    "lgtm":[".sarif"],
    "checkov":[".json"],
    "semgrep":[".json"],
    "bridgecrew":[".json", ".csv"],
    "securityscorecard":[".csv"],
    "amazon guardduty": [".json"],
    "amazon inspector":[".json"],
    "burpsuite enterprise":[".html"],
    "orca security":[".json"],
    "edgescan":[".json"],
    "nodezero":[".json"],
    "whitehat sentinel":[".json", ".csv"],
    "gitguardian": [".json", ".zip"],
    "appknox":[".json"],
    "safety": [".json"],
    "rapid7 insightvm":[".xml",".json"],
    "gosec scanner": [".json", ".csv"],
    "tanium":[".csv", ".zip"],
    "salt security": [".json"],
    "grammatech - codesentry": [".json"],
    "grammatech - codesonar": [".json",  ".csv", ".xml"],
    'traceable': [".json"],
    'checkmarx sca': [".json"],
    'sonrai security': [".json"],
    "data theorem": [".json"],
    "crowdstrike falcon": [".json", ".csv"],
    "cloud optix": [".json", ".csv"],
    "sysdig": [".csv", ".zip"],
    "stackhawk": [".json"],
    "github secret scan": [".json"],
    "checkmarx one": [".json"],
    "grype": [".json"],
    "jfrog vision (vdoo)": [".json"],
    "probely": [".json"],
    "hashicorp vault radar": [".json", ".csv"],
    "runzero": [".json", ".csv"],
    "mergebase": [".json"],
    "trivy": [".json"],
    "finitestate": [".json"],
    "klocwork": [".json"],
    "spectral": ['.csv', '.json'],
    "seeker": [".json"],
    "clayton": [".json"],
    "apiiro": [".json"],
    "tenable.sc":['.csv', '.json'],
    "coverity on polaris": [".json"],
    "cortex xdr": [".json"],
    "iriusrisk": [".json"],
    "sd elements": [".json"],
    "meterian": [".json"],
    "tenable cloud security": [".json"],
    "redhat acs": [".json"],
    "halo security": [".json"],
    "cycode": [".json"],
    "gitlab security": [".json"],
    "deepfence": [".json"],
    "mend.io": [".json"],
    "microsoft defender for cloud": [".csv", ".zip"],
    "upguard": [".zip"],
    "bitsight": [".json", ".csv"],
    "42crunch": [".json"],
    "microsoft defender for endpoint": [".json", ".csv", ".zip"],
    "sentinelone": [".csv", ".zip"],
    "noname":[".json"],
    "polyspace": [".json"],
    "appcheck": [".json"],
    "endor labs": [".csv",".json"],
    "legitify": [".json"],
    "docker scout": [".sarif", ".sbom"],
    "wpscan": [".json"],
    "riskrecon" : [".json",".csv", ".zip", ".txt"],
    "prowler" : [".json"],
    "privado": [".json"],
    "oligo security":[".json"],
    "orca security shiftleft": [".zip"],
    "appsentinels": [".zip",".csv"],
    "synopsys polaris": [".json"],
    "socket.dev": [".json", ".zip"],
    "rapid7 insight cloud security": [".json", ".zip"],
    "acunetix premium" : [".json"],
    "trufflehog": [".json"],
    "prisma cloud compute": [".csv"],
    "qualys container security": [".json", ".zip"],
    "qualys policy compliance": [".json", ".zip"],
    "arnica": [".txt", ".json"],
    "blackduck binary analysis": [".json"],
    "hcl appscan enterprise": [".xml", ".json"]
}

const ScanNameMap = {
    zap: "ZAP Scan",
    bandit: "Bandit Scan",
    burpsuite: "Burp Scan",
    "dependency check": "Dependency Check Scan",
    "dependency_check": "Dependency Check Scan",
    eslint: "ESLint Scan",
    fortify: "Fortify Scan",
    // sonarqube: "SonarQube Scan",
    sonarqube: "SonarQube Scan detailed",
    sonarcloud: "SonarCloud",
    "blackduck hub": "Blackduck Hub Scan",
    "blackduck_hub": "Blackduck Hub Scan",
    checkmarx: "Checkmarx Scan detailed",
    "jfrog xray": "JFrog Xray",
    "npm audit": "NPM Audit Scan",
    "qualys": "Qualys Scan", 
    "qualys webapp": "Qualys WebApp Scan",
    "qualys infrastructure": "Qualys Infrastructure Scan (WebGUI XML)",
    acunetix: "Acunetix Scan",
    metasploit: "Metasploit",
    appscan: "IBM AppScan DAST",
    appspider: "Appspider Scan",
    aqua: "Aqua Scan",
    hackerone: "HackerOne Cases",
    "aws prowler": "AWS Prowler Scan",
    "aws security hub": "AWS Security Hub",
    brakeman: "Brakeman Scan",
    bugcrowd: "Bugcrowd Scan",
    clair: "Clair Scan",
    cobalt: "Cobalt",
    contrast: "Contrast Scan",
    gitleaks: "Gitleaks Scan",
    "microfocus webinspect": "Microfocus Webinspect Scan",
    "mozilla observatory": "Mozilla Observatory Scan",
    nessus: "Nessus Scan",
    netsparker: "Netsparker",
    nikto: "Nikto Scan",
    nmap: "NMAP Scan",
    "openvas csv": "Openvas CSV",
    snyk: "Snyk Scan",
    sqreen: "Sqreen Scan",
    trustwave: "Trustwave Scan",
    'prisma cloud twistlock': "Prisma Cloud Twistlock",
    veracode: "Veracode Scan",
    "web inspect": "Microfocus Webinspect Scan",
    mend: "Mend Scan",
    "contrast security": "Contrast Security",
    "nowsecure": "NowSecure Scan",
    "dependency track": "Dependency Track",
    "dependency_track": "Dependency Track",
    "cyclonedx": "CycloneDx",
    "qwiet ai": "Qwiet AI",
    coverity: "Coverity Scan",
    "rapid7 nexpose": "Rapid7 nexpose",
    "detect secrets": "Detect Secrets Scan",
    "dependabot": "Dependabot",
    'github code scan':'Github Code Scan',
    'github_code_scan':'Github Code Scan',
    'prisma cloud redlock':'Prisma Cloud Redlock',
    "google cloud registry":'Google Cloud Registry',
    avocado: 'avocado',
    "vm securestate": "VM SecureState",
    lacework: 'lacework',
    "sonatype lifecycle": 'Sonatype Lifecycle',
    "magento" : 'Magento',
    "rapid7 insight": 'Rapid7 Insight',
    "kics":'Kics',
    "yarn audit": "Yarn Audit",
    "bundler audit": "Bundler Audit",
    "tenable webapp": "Tenable Webapp",
    "tenable infrastructure": "Tenable Infrastructure",
    "scout suite": "Scout Suite",
    "wiz":"Wiz",
    "lgtm":"LGTM",
    "checkov":"Checkov",
    "semgrep":"Semgrep",
    "bridgecrew":"Bridgecrew",
    "securityscorecard":"SecurityScorecard",
    "amazon guardduty": "Amazon GuardDuty",
    "amazon inspector": "Amazon Inspector",
    "burpsuite enterprise": "Burpsuite Enterprise",
    "orca security": "Orca Security",
    "edgescan":"Edgescan",
    "nodezero": "NodeZero",
    "whitehat sentinel":"WhiteHat Sentinel",
    "gitguardian": "GitGuardian",
    "appknox": "Appknox",
    "safety": "Safety",
    "rapid7 insightvm":"Rapid7 InsightVM",
    "gosec scanner": "Gosec Scanner",
    "tanium":"Tanium",
    "salt security": "Salt Security",
    "grammatech - codesentry": "Grammatech - CodeSentry",
    "grammatech - codesonar": "Grammatech - CodeSonar",
    'traceable': "Traceable",
    'checkmarx sca': "Checkmarx SCA",
    'sonrai security': "Sonrai Security",
    "data theorem": "Data Theorem",
    "cloud optix": "Cloud Optix",
    "crowdstrike falcon": "CrowdStrike Falcon",
    "sysdig": "SysDig",
    "stackhawk": "Stackhawk",
    "github secret scan": "Github Secret Scan",
    "checkmarx one": "Checkmarx One",
    "grype": "Grype",
    "jfrog vision (vdoo)": "JFrog Vision (Vdoo)",
    "probely": "Probely",
    "hashicorp vault radar":  "HashiCorp Vault Radar",
    "runzero": "RunZero",
    "mergebase": "Mergebase",
    "trivy": "Trivy Scan",
    "finitestate": "FiniteState",
    "klocwork": "Klocwork Scan",
    "spectral": "Spectral",
    "seeker": "Seeker",
    "clayton": "Clayton",
    "apiiro": "Apiiro",
    "tenable.sc":"Tenable.sc",
    "coverity on polaris": "Coverity on Polaris",
    "cortex xdr":"Cortex XDR",
    "iriusrisk": "IriusRisk",
    "sd elements": "SD Elements",
    "meterian": "Meterian",
    "tenable cloud security": "Tenable Cloud Security",
    "redhat acs": "Redhat ACS",
    "halo security": "Halo Security",
    "cycode": "Cycode",
    "gitlab security": "Gitlab Security",
    "deepfence": "Deepfence",
    "mend.io": "Mend.io Scan",
    "tenable vulnerability management": "Tenable Vulnerability Management",
    "microsoft defender for cloud": "Microsoft Defender for Cloud",
    "upguard": "UpGuard",
    "bitsight": "BitSight",
    "42crunch": "42Crunch",
    "microsoft defender for endpoint": "Microsoft Defender for Endpoint",
    "sentinelone":  "SentinelOne",
    "noname":"Noname",
    "polyspace": "Polyspace",
    "appcheck": "AppCheck",
    "endor labs": "Endor Labs",
    "legitify" : "Legitify",
    "docker scout": "Docker Scout",
    "wpscan": "WPScan",
    "riskrecon": "RiskRecon",
    "prowler" : "Prowler",
    "privado": "Privado",
    "oligo security": "Oligo Security",
    "orca security shiftleft": "Orca Security Shiftleft",
    "appsentinels": "AppSentinels",
    "synopsys polaris": "Synopsys Polaris",
    "socket.dev": "Socket.dev",
    "rapid7 insight cloud security": "Rapid7 Insight Cloud Security",
    "acunetix premium": "Acunetix Premium",
    "trufflehog": "TruffleHog",
    "prisma cloud compute": "Prisma Cloud Compute",
    "qualys container security": "Qualys Container Security",
    "qualys policy compliance": "Qualys Policy Compliance",
    "arnica": "Arnica",
    "blackduck binary analysis": "Blackduck Binary Analysis",
    "hcl appscan enterprise": "HCL AppScan Enterprise",
}

function getAPIKeyFields(data,list){
    const fields = [{
        render: () => {
            return (
                <Alert message="Why do you need an API key?" description="The API key will be used by the script to authenticate the report ingestion request." showIcon className="m-b-sm" />
            )
        }
    }, {
        name: "keyExists",
        label: "I have already generated an API key",
        type: FieldTypes.BOOLEAN,
        inline: true
    }, {
        name: "apiName",
        // adding asterisk manually, as the validator and required messages are overlapping
        label: <>{!data.keyExists ? <span style={{color : "red", fontSize: 18,marginRight: '4px'}}>*</span> : ""}Enter a key name</>,
        placeholder: "Enter a key name",
        disabled: data.keyExists,
        rules: [ 
            nameRegexValidator({min: 3}),
        {
            validator: (_,value) =>{
                if(!value && !data.keyExists){
                    return Promise.reject("Token Name is required")
                }
                if(value && !data.keyExists){
                    for(const object of list){
                        if(object.name === value){
                            return Promise.reject("Token Name already exists")
                         }
                      }
                         return Promise.resolve()
                }
                return Promise.resolve()  
        }
        }]
    }, {
        name: "expiry",
        label: "Expiry",
        disabled: data.keyExists,
        type: FieldTypes.SELECT,
        options: [{ label: "Never", value: -1 }, { label: "3 months", value: 90 }, { label: "6 months", value: 180 }, { label: "1 year", value: 365 }],
        rules: [{
            required: !data.keyExists,
            message: "Date is required"
        }]
    }];
    return fields;
}

export function getCommonFields({products, subProducts, environments, tool,versions, allowZip, isEdit, hasDisabledEnvironment}){
    const commonFields = [
    {
        name: "product",
        label: <Vocabulary name="product" />,
        rules: !isEdit && [{required: true, message: <>Please select a <Vocabulary name="product" isLowerCase={true}/></>}],
        type: FieldTypes.SELECT,
        options: products,
        loading: _.isEmpty(products),
        help: <>ArmorCode <Vocabulary name="product" /> name in which we need to ingest the findings.</>,
        colSpan: 12,
        disabled: isEdit,
        props: {
            autoFocus: !isEdit
        }
    }, {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        rules: !isEdit && [{required: true, message: <>Please select a <Vocabulary name="subProduct"/></>}],
        type: FieldTypes.SELECT,
        options: subProducts,
        loading: _.isNull(subProducts),
        help: <>ArmorCode <Vocabulary name="subProduct" /> name of the selected <Vocabulary name="product" /> in which we need to ingest the findings.</>,
        colSpan: 12,
        disabled: isEdit,
    }, ...(hasDisabledEnvironment ? [] : [{
        name: "environment",
        label: "Environment",
        rules: !isEdit && [{required: true, message: "Please select an environment"}],
        type: FieldTypes.SELECT,
        options: envParseSelectOptions(environments),
        loading: _.isNull(environments),
        help: <>ArmorCode Environment of the selected <Vocabulary name="subProduct" /> in which we need to ingest the findings. Such as <EnvironmentName name={"Production"} />, <EnvironmentName name={"Staging"} />, etc.</>,
        colSpan: 12,
        disabled: isEdit,
    }]),{
        name: "folder_name",
        label: "Directory",
        rules: [{required: true, message: "Please enter the report location"}, directoryRegexValidator()],
        help: "Full path of the folder where scan files are getting stored. Such as '/home/tools/tool_name'",
        colSpan: 12,
        props: {
            autoFocus: isEdit
        }
    }, /* {
        name: "file_name_prefix",
        label: "File prefix",
        help: <>Prefix of the scan files to avoid any other reports to be picked as the scan file. <br />Ex: For Zap if we store all files with names starting with 'zap' such as zap_scan_123.xml then filename_prefix would be 'zap'.</>,
        colSpan: 12
    },  */{
        name: "file_format",
        label: "File extension",
        rules: !(_.size(ExtensionsMap[tool]) <= 1) && [{required: true, message: "Please select the report file extension"}],
        type: FieldTypes.SELECT,
        options: ExtensionsMap[tool],
        disabled: _.size(ExtensionsMap[tool]) <= 1,
        help: "File extension of the scan files, it's predefined for all the tools. Such as '.html' for SonarQube.",
        colSpan: 12
    }];
    if(allowZip){
        commonFields.push({
            name: "allowZip",
            label: "Allow zip upload",
            help: "Script will compress all the scan files into a zip and push it as a single scan.",
            type: FieldTypes.BOOLEAN,
            inline: true,
            colSpan: 12
        })
    }
    return commonFields;
}
